import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TimeFilterEnum } from "@/types/eventFilter";
import { EventStatusEnum, statusLabels } from "@/types/eventStatus";

interface EventFilterComponentProps {
  selectedStatuses: EventStatusEnum[];
  companyName: string;
  contactName: string;
  hasVacancies: boolean | undefined;
  timeFilter: TimeFilterEnum | undefined;
  startDateFrom: string;
  startDateTo: string;
  endDateFrom: string;
  endDateTo: string;

  onStatusesChange: (statuses: EventStatusEnum[]) => void;
  onCompanyNameChange: (name: string) => void;
  onContactNameChange: (name: string) => void;
  onHasVacanciesChange: (hasVacancies: boolean | undefined) => void;
  onTimeFilterChange: (filter: TimeFilterEnum | undefined) => void;
  onStartDateFromChange: (date: string) => void;
  onStartDateToChange: (date: string) => void;
  onEndDateFromChange: (date: string) => void;
  onEndDateToChange: (date: string) => void;

  allowedStatuses: EventStatusEnum[];
  showStatusFilter?: boolean;
  showTimeFilter?: boolean;
  showVacancyFilter?: boolean;
  showDateFilters?: boolean;
  showClientFilters?: boolean;
  showCompanyNameFilter?: boolean;
}

const filterButtonClass =
  "transition-colors px-3 py-1 text-xs font-medium rounded-full border";

export function EventFilterComponent({
  selectedStatuses,
  companyName,
  contactName,
  hasVacancies,
  timeFilter,
  startDateFrom,
  startDateTo,
  endDateFrom,
  endDateTo,
  onStatusesChange,
  onCompanyNameChange,
  onContactNameChange,
  onHasVacanciesChange,
  onTimeFilterChange,
  onStartDateFromChange,
  onStartDateToChange,
  onEndDateFromChange,
  onEndDateToChange,
  allowedStatuses,
  showStatusFilter = true,
  showTimeFilter = true,
  showVacancyFilter = true,
  showDateFilters = true,
  showClientFilters = true,
  showCompanyNameFilter = true,
}: EventFilterComponentProps) {
  const toggleStatus = (status: EventStatusEnum) => {
    const newStatuses = selectedStatuses.includes(status)
      ? selectedStatuses.filter((s) => s !== status)
      : [...selectedStatuses, status];
    onStatusesChange(newStatuses);
  };

  return (
    <div className="mb-6">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="font-semibold px-6 py-2 rounded-lg border"
          >
            Filters
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[500px] p-5 space-y-6 bg-card border border-border rounded-lg shadow-lg">
          {/* Event/Company Name Search */}
          {showCompanyNameFilter && (
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Search by Company Name
              </span>
              <Input
                placeholder="Type to search..."
                value={companyName}
                onChange={(e) => onCompanyNameChange(e.target.value)}
                className="w-full"
              />
            </div>
          )}

          {/* Client Name Search */}
          {showClientFilters && (
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Search by Contact Name
              </span>
              <Input
                placeholder="Type to search..."
                value={contactName}
                onChange={(e) => onContactNameChange(e.target.value)}
                className="w-full"
              />
            </div>
          )}

          {/* Status Multi-Select */}
          {showStatusFilter && (
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Status
              </span>
              <div className="flex gap-2 flex-wrap">
                {allowedStatuses.map((status) => (
                  <Button
                    key={status}
                    size="sm"
                    variant={
                      selectedStatuses.includes(status) ? "default" : "outline"
                    }
                    onClick={() => toggleStatus(status)}
                    className={cn(
                      filterButtonClass,
                      selectedStatuses.includes(status)
                        ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                        : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                    )}
                  >
                    {statusLabels[status]}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Time Filter */}
          {showTimeFilter && (
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Time Period
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={!timeFilter ? "default" : "outline"}
                  onClick={() => onTimeFilterChange(undefined)}
                  className={cn(
                    filterButtonClass,
                    !timeFilter
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={
                    timeFilter === TimeFilterEnum.UPCOMING
                      ? "default"
                      : "outline"
                  }
                  onClick={() => onTimeFilterChange(TimeFilterEnum.UPCOMING)}
                  className={cn(
                    filterButtonClass,
                    timeFilter === TimeFilterEnum.UPCOMING
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  Upcoming
                </Button>
                <Button
                  size="sm"
                  variant={
                    timeFilter === TimeFilterEnum.PAST ? "default" : "outline"
                  }
                  onClick={() => onTimeFilterChange(TimeFilterEnum.PAST)}
                  className={cn(
                    filterButtonClass,
                    timeFilter === TimeFilterEnum.PAST
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  Past
                </Button>
              </div>
            </div>
          )}

          {/* Has Vacancies Filter */}
          {showVacancyFilter && (
            <div>
              <span className="block font-semibold text-foreground mb-2">
                Staff Vacancies
              </span>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={hasVacancies === undefined ? "default" : "outline"}
                  onClick={() => onHasVacanciesChange(undefined)}
                  className={cn(
                    filterButtonClass,
                    hasVacancies === undefined
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={hasVacancies === true ? "default" : "outline"}
                  onClick={() => onHasVacanciesChange(true)}
                  className={cn(
                    filterButtonClass,
                    hasVacancies === true
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  Has Vacancies
                </Button>
                <Button
                  size="sm"
                  variant={hasVacancies === false ? "default" : "outline"}
                  onClick={() => onHasVacanciesChange(false)}
                  className={cn(
                    filterButtonClass,
                    hasVacancies === false
                      ? "bg-primary border-primary text-primary-foreground hover:bg-primary/90"
                      : "border-border bg-background hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  Fully Staffed
                </Button>
              </div>
            </div>
          )}

          {/* Date Filters */}
          {showDateFilters && (
            <div className="space-y-4">
              <div>
                <span className="block font-semibold text-foreground mb-2">
                  Start Date Range
                </span>
                <div className="flex gap-3">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-muted-foreground mb-1 block">
                      From
                    </span>
                    <Input
                      type="datetime-local"
                      value={startDateFrom}
                      onChange={(e) => onStartDateFromChange(e.target.value)}
                      className="w-full text-sm"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-muted-foreground mb-1 block">
                      To
                    </span>
                    <Input
                      type="datetime-local"
                      value={startDateTo}
                      onChange={(e) => onStartDateToChange(e.target.value)}
                      className="w-full text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <span className="block font-semibold text-foreground mb-2">
                  End Date Range
                </span>
                <div className="flex gap-3">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-muted-foreground mb-1 block">
                      From
                    </span>
                    <Input
                      type="datetime-local"
                      value={endDateFrom}
                      onChange={(e) => onEndDateFromChange(e.target.value)}
                      className="w-full text-sm"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-muted-foreground mb-1 block">
                      To
                    </span>
                    <Input
                      type="datetime-local"
                      value={endDateTo}
                      onChange={(e) => onEndDateToChange(e.target.value)}
                      className="w-full text-sm"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
